// [signal-decisions-v1] Trading UI client for /api/v1/signal/decisions.
//
// This is the canonical decision-first feed that drives the top
// "Premarket Signal Decisions" table on the trading UI. The legacy
// /api/v1/signal/candidates feed remains in place to power the
// secondary "Raw Ranking Inputs" panel below.
//
// Honest-empty contract: no fabricated probabilities. Decision fields
// come from the backend's deterministic
// market_pulse.matrix.directional_decision module.

export type SignalBias = "Bullish" | "Bearish" | "Neutral";

export type SignalUse =
  | "Buy Candidate"
  | "Short Candidate"
  | "Reversal Watch"
  | "Avoid";

export type SignalTrend = "Continuing" | "Reversing" | "Unknown";

export type SignalConfidence = "High" | "Medium" | "Low";

export type SignalDataQuality =
  | "live"
  | "after_hours"
  | "delayed"
  | "stale"
  | "unavailable"
  | "mismatched";

// ADV-SIGNAL-SAFE-LABELS — safe display vocabulary for the /signal page. The backend
// directional_decision emits DISPLAY-style signal_use strings ("Buy Candidate" /
// "Short Candidate" / "Reversal Watch" / "Avoid"); these are display-only, not a
// persisted contract, so we normalize them to safe watch/review language at render
// time. NO action-style labels are ever shown; backend decision math is unchanged.
export type SafeSignalUse =
  | "Buy Candidate"
  | "Short Candidate"
  | "Watchlist Candidate"
  | "Short Watch"
  | "Review Required"
  | "Monitor"
  | "Ignore"
  | "Do Not Act"
  | "Data Stale"
  | "Low Quality"
  | "High Risk";

export interface SafeSignalLabel { label: SafeSignalUse; key: string; }

export const SAFE_SIGNAL_LABELS: ReadonlySet<string> = new Set<string>([
  "Buy Candidate", "Short Candidate",
  "Watchlist Candidate", "Short Watch", "Review Required", "Monitor",
  "Ignore", "Do Not Act", "Data Stale", "Low Quality", "High Risk",
]);

/**
 * Fail-closed display normalization for a directional decision. Precedence:
 *   1. stale data           -> Data Stale
 *   2. unusable/mismatched   -> Do Not Act
 *   3. explicit avoid / "do not act" reason -> Do Not Act
 *   4. low confidence        -> Review Required
 *   5. base map: buy_candidate->Watchlist Candidate, short_candidate->Short Watch,
 *               reversal_watch->Monitor
 *   6. unknown               -> Review Required (fail closed)
 * Never returns an action-style label (Buy/Short Candidate/Buy/Sell/Execute).
 */
export function toSafeSignalUse(d: {
  signal_use?: string | null;
  data_quality?: string | null;
  confidence?: string | null;
  reason?: string | null;
}): SafeSignalLabel {
  const raw = String(d.signal_use || "").toLowerCase().replace(/\s+/g, "_");
  const dq = String(d.data_quality || "").toLowerCase();
  const conf = String(d.confidence || "").toLowerCase();
  const reason = String(d.reason || "").toLowerCase();

  if (dq === "stale") return { label: "Data Stale", key: "data_stale" };
  if (dq === "mismatched" || dq === "unavailable" || dq === "") return { label: "Do Not Act", key: "do_not_act" };
  if (raw === "avoid" || /\b(do not act|do not trade|avoid)\b/.test(reason)) return { label: "Do Not Act", key: "do_not_act" };
  if (conf === "low") return { label: "Review Required", key: "review_required" };
  // ADV-P0N: category label restored (separate from action status below).
  if (raw === "buy_candidate") return { label: "Buy Candidate", key: "buy_candidate" };
  if (raw === "short_candidate") return { label: "Short Candidate", key: "short_candidate" };
  if (raw === "reversal_watch") return { label: "Monitor", key: "monitor" };
  return { label: "Review Required", key: "review_required" };
}

export interface SignalDirectionalDecision {
  bias: SignalBias;
  signal_use: SignalUse;
  trend_state: SignalTrend;
  confidence: SignalConfidence;
  reason: string;
  invalidation: string;
  data_quality: SignalDataQuality;
  label_reason: string | null;
}

export interface SignalDecisionRow {
  symbol: string;
  rank: number | null;
  price: number | null;
  premarket_gap_pct: number | null;
  spread_bps: number | null;
  premarket_volume: number | null;
  float_value: number | null;
  float_class: string | null;
  score?: number | null;
  directional_decision: SignalDirectionalDecision;
}

export interface SignalDecisionSummary {
  bullish_candidates: number;
  bearish_candidates: number;
  reversal_watch: number;
  avoid: number;
  stale_or_unavailable: number;
}

export interface SignalDecisionsPayload {
  ok: boolean;
  as_of_utc: string;
  as_of_et: string;
  market_phase: "premarket" | "regular" | "afterhours";
  candidate_source_status: string;
  candidate_source_age_seconds: number;
  summary: SignalDecisionSummary;
  rows: SignalDecisionRow[];
  decisions: Array<{ symbol: string } & SignalDirectionalDecision>;
  schema_version: number;
}

export async function getSignalDecisions(): Promise<SignalDecisionsPayload> {
  const res = await fetch("/api/v1/signal/decisions", {
    cache: "no-store",
  });
  const body = await res.json();
  // The backend wraps with {ok, data, ...}; unwrap.
  if (body?.data) return body.data as SignalDecisionsPayload;
  return body as SignalDecisionsPayload;
}


// ADV-P0N: Action/execution status is a SEPARATE concept from the category label.
// A candidate category is never an order instruction; this status states whether the
// row is actionable and always affirms the system never auto-trades. Mirrors the same
// safety precedence (stale / unavailable / avoid / low-confidence) as the category.
export interface SignalActionStatus { label: string; key: string; }
export function signalActionStatus(d: {
  signal_use?: string | null;
  data_quality?: string | null;
  confidence?: string | null;
  reason?: string | null;
}): SignalActionStatus {
  const raw = String(d.signal_use || "").toLowerCase().replace(/\s+/g, "_");
  const dq = String(d.data_quality || "").toLowerCase();
  const conf = String(d.confidence || "").toLowerCase();
  const reason = String(d.reason || "").toLowerCase();
  if (dq === "stale") return { label: "Not Actionable \u00b7 Data stale", key: "not_actionable_stale" };
  if (dq === "mismatched" || dq === "unavailable" || dq === "") return { label: "Not Actionable \u00b7 Quote unavailable", key: "not_actionable_unavailable" };
  if (raw === "avoid" || /\b(do not act|do not trade|avoid)\b/.test(reason)) return { label: "Not Actionable", key: "not_actionable" };
  if (conf === "low") return { label: "Needs Confirmation", key: "needs_confirmation" };
  return { label: "Watch Only \u00b7 No Auto-Trade", key: "watch_only" };
}
